import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { Dashboard } from "@/pages/Dashboard";
import { LandCadastre } from "@/pages/LandCadastre";
import { MiningCadastre } from "@/pages/MiningCadastre";
import { Survey } from "@/pages/Survey";
import { Registry } from "@/pages/Registry";
import { Applications } from "@/pages/Applications";
import { ApplicationDetail } from "@/pages/ApplicationDetail";
import { Compliance } from "@/pages/Compliance";
import { Inspections } from "@/pages/Inspections";
import { Disputes } from "@/pages/Disputes";
import { Revenue } from "@/pages/Revenue";
import { Knowledge } from "@/pages/Knowledge";
import { Intelligence } from "@/pages/Intelligence";
import { PublicPortal } from "@/pages/PublicPortal";
import { PublicSearch } from "@/pages/PublicSearch";
import { PublicMap } from "@/pages/PublicMap";
import { VerifyCertificate } from "@/pages/VerifyCertificate";
import { CapabilityRegister } from "@/pages/CapabilityRegister";
import { ApplicantPortal } from "@/pages/ApplicantPortal";
import { IntegrityCentre } from "@/pages/IntegrityCentre";
import { SpatialValidation } from "@/pages/SpatialValidation";
import { DigitisationCentre } from "@/pages/DigitisationCentre";
import { Reports } from "@/pages/Reports";
import { Briefings } from "@/pages/Briefings";
import { Notifications } from "@/pages/Notifications";
import { Performance } from "@/pages/Performance";
import { DigitalTwin } from "@/pages/DigitalTwin";
import { IntegrationHub } from "@/pages/IntegrationHub";
import { AnalyticsCentre } from "@/pages/AnalyticsCentre";
import { ComplianceReport } from "@/pages/ComplianceReport";
import { AuditRegister } from "@/pages/AuditRegister";
import { HistoricalTimeline } from "@/pages/HistoricalTimeline";
import { MigrationCentre } from "@/pages/MigrationCentre";
import { MigrationSituationRoom } from "@/pages/MigrationSituationRoom";
import { MiningMigrationCentre } from "@/pages/MiningMigrationCentre";
import { MiningMigrationCommand } from "@/pages/MiningMigrationCommand";
import { MiningSurveyorPortal } from "@/pages/MiningSurveyorPortal";
import { MiningClaimantPortal } from "@/pages/MiningClaimantPortal";
import { EnterpriseIntelligence } from "@/pages/EnterpriseIntelligence";
import { AdminConsole } from "@/pages/AdminConsole";
import { VerifyEvidencePack } from "@/pages/VerifyEvidencePack";
import { CertificatePortal } from "@/pages/CertificatePortal";
import { OperationalReadiness } from "@/pages/OperationalReadiness";
import { NationalDeployment } from "@/pages/NationalDeployment";
import { GlobalGovernance } from "@/pages/GlobalGovernance";
import { MineralGovernance } from "@/pages/MineralGovernance";
import { MiningInvestorPortal } from "@/pages/MiningInvestorPortal";
import { MineralPlus } from "@/pages/MineralPlus";
import { SovereignIntelligenceCentre } from "@/pages/SovereignIntelligenceCentre";
import { GSROS } from "@/pages/GSROS";
import { ExecutiveAttention } from "@/pages/ExecutiveAttention";
import { RoleLanding } from "@/pages/RoleLandings";
import { ExecutiveWatchlist } from "@/pages/ExecutiveWatchlist";
import { Boardroom } from "@/pages/Boardroom";
import { ExecutiveTimeline } from "@/pages/ExecutiveTimeline";
import { EnvironmentalGovernance } from "@/pages/EnvironmentalGovernance";
import { ThomArchitecture } from "@/pages/ThomArchitecture";
import { CrossPillarIntelligence } from "@/pages/CrossPillarIntelligence";
import { SovereignCockpit } from "@/pages/SovereignCockpit";
import { NationalValueCentre } from "@/pages/NationalValueCentre";
import { NationalTransformationCentre } from "@/pages/NationalTransformationCentre";
import { NationalExecutiveIntelligence } from "@/pages/NationalExecutiveIntelligence";
import { SovereignOperatingModel } from "@/pages/SovereignOperatingModel";
import { CabinetWorkspaces } from "@/pages/CabinetWorkspaces";
import { Province360 } from "@/pages/Province360";
import { GovernmentExecutiveCanvas } from "@/pages/GovernmentExecutiveCanvas";
import { PhaseXVICentre } from "@/pages/PhaseXVICentre";
import { PublicVerification } from "@/pages/PublicVerification";
import { NationalExecutiveDashboard } from "@/pages/NationalExecutiveDashboard";
import { ExecutiveLandingRouter } from "@/pages/ExecutiveLandingRouter";
import { CabinetExecutionEngine } from "@/pages/CabinetExecutionEngine";
import { SovereignPerformancePlatform } from "@/pages/SovereignPerformancePlatform";
import { NationalOutcomesCentre } from "@/pages/NationalOutcomesCentre";
import { StrategicDecisionCentre } from "@/pages/StrategicDecisionCentre";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#0B3558",
              color: "#F6F8FA",
              border: "1px solid rgba(201,154,46,0.4)",
              fontFamily: "IBM Plex Sans, sans-serif",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/portal" element={<PublicPortal />} />
          <Route path="/portal/search" element={<PublicSearch />} />
          <Route path="/portal/map" element={<PublicMap />} />
          <Route path="/verify/:ref" element={<VerifyCertificate />} />
          <Route path="/verify/pack/:packNumber" element={<VerifyEvidencePack />} />
          <Route path="/certificates" element={<CertificatePortal />} />
          <Route path="/status" element={<OperationalReadiness />} />
          <Route path="/portal/mining-investor" element={<MiningInvestorPortal />} />
          <Route path="/verify" element={<PublicVerification />} />
          <Route path="/verify-document/:docId" element={<PublicVerification />} />

          <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
            <Route index element={<ExecutiveLandingRouter />} />
            <Route path="operational-dashboard" element={<Dashboard />} />
            <Route path="intelligence" element={<Intelligence />} />
            <Route path="land" element={<LandCadastre />} />
            <Route path="mining" element={<MiningCadastre />} />
            <Route path="survey" element={<Survey />} />
            <Route path="registry" element={<Registry />} />
            <Route path="applications" element={<Applications />} />
            <Route path="applications/:aid" element={<ApplicationDetail />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="inspections" element={<Inspections />} />
            <Route path="disputes" element={<Disputes />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="knowledge" element={<Knowledge />} />
            <Route path="public-preview" element={<PublicPortal />} />
            <Route path="capability-register" element={<CapabilityRegister />} />
            <Route path="portal" element={<ApplicantPortal />} />
            <Route path="integrity" element={<IntegrityCentre />} />
            <Route path="spatial" element={<SpatialValidation />} />
            <Route path="digitisation" element={<DigitisationCentre />} />
            <Route path="reports" element={<Reports />} />
            <Route path="briefings" element={<Briefings />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="performance" element={<Performance />} />
            <Route path="twin" element={<DigitalTwin />} />
            <Route path="integration" element={<IntegrationHub />} />
            <Route path="analytics" element={<AnalyticsCentre />} />
            <Route path="compliance-report" element={<ComplianceReport />} />
            <Route path="audit-register" element={<AuditRegister />} />
            <Route path="history" element={<HistoricalTimeline />} />
            <Route path="migration" element={<MigrationCentre />} />
            <Route path="migration/situation" element={<MigrationSituationRoom />} />
            <Route path="mining-migration" element={<MiningMigrationCentre />} />
            <Route path="mining-migration/command" element={<MiningMigrationCommand />} />
            <Route path="mining-migration/surveyors" element={<MiningSurveyorPortal />} />
            <Route path="mining-migration/claimants" element={<MiningClaimantPortal />} />
            <Route path="enterprise-intelligence" element={<EnterpriseIntelligence />} />
            <Route path="admin-console" element={<AdminConsole />} />
            <Route path="national-deployment" element={<NationalDeployment />} />
            <Route path="global-governance" element={<GlobalGovernance />} />
            <Route path="mineral-governance" element={<MineralGovernance />} />
            <Route path="mineral-plus" element={<MineralPlus />} />
            <Route path="sovereign-intelligence" element={<SovereignIntelligenceCentre />} />
            <Route path="gsros" element={<GSROS />} />
            <Route path="executive" element={<ExecutiveAttention />} />
            <Route path="my-workspace" element={<RoleLanding />} />
            <Route path="watchlist" element={<ExecutiveWatchlist />} />
            <Route path="timeline" element={<ExecutiveTimeline />} />
            <Route path="boardroom" element={<Boardroom />} />
            <Route path="environment" element={<EnvironmentalGovernance />} />
            <Route path="thom360" element={<ThomArchitecture />} />
            <Route path="cross-pillar" element={<CrossPillarIntelligence />} />
            <Route path="cockpit" element={<SovereignCockpit />} />
            <Route path="value" element={<NationalValueCentre />} />
            <Route path="transformation" element={<NationalTransformationCentre />} />
            <Route path="executive-intelligence" element={<NationalExecutiveIntelligence />} />
            <Route path="operating-model" element={<SovereignOperatingModel />} />
            <Route path="cabinet-workspaces" element={<CabinetWorkspaces />} />
            <Route path="province360" element={<Province360 />} />
            <Route path="executive-canvas" element={<GovernmentExecutiveCanvas />} />
            <Route path="sovereign-readiness" element={<PhaseXVICentre />} />
            <Route path="national-executive-dashboard" element={<NationalExecutiveDashboard />} />
            <Route path="cabinet-execution" element={<CabinetExecutionEngine />} />
            <Route path="sovereign-performance" element={<SovereignPerformancePlatform />} />
            <Route path="national-outcomes" element={<NationalOutcomesCentre />} />
            <Route path="strategic-decisions" element={<StrategicDecisionCentre />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
